import * as React from 'react';
import {
    CreateButton,
    DateField,
    FilterButton,
    List,
    NumberField,
    SearchInput,
    TopToolbar,
    TextField,
    DatagridConfigurable,
    SelectColumnsButton,
    useTranslate,
    FunctionField,
    useNotify,
    WithListContext,
    DatagridLoading,
    Pagination,
    BulkDeleteButton,
} from 'react-admin';
import { useMediaQuery, Theme } from '@mui/material';
import { Alert } from '@mui/material';
import { useCanAccess } from '@react-admin/ra-rbac';

import CustomStatusField from '../controls/CustomStatusField';
import UserListAside from './UserListAside';
import get from 'lodash/get';
import CustomPagination from '../common/CustomPagination';
import SourceNameField from './SourceNameField';
import BulkChangeRoleButton from './BulkChangeRoleButton';
import BulkChangeEnvsButton from './BulkChangeEnvsButton';
// import ProductFullNameField from '../product/ProductFullNameField';
// import SubscriberFullNameField from './SubscriberFullNameField';

const ADMIN_APP_GUID = '13b312b5-367c-4c74-bb77-bdb34df4e721';
const USERRIGHTS_MAP: Record<number, string> = {
    250: 'Tuotepäivittäjä',
    300: 'Developer',
    350: 'Pääkäyttäjä',
    400: 'Järjestelmänvalvoja',
};
const ENVS_MAP: Record<string, string> = {
    prod: 'Tuotanto',
    next: 'Next',
    dev: 'Dev',
};

const userFilters = [<SearchInput source="q" alwaysOn />];

const ListActions = () => {
    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    return (
        <TopToolbar>
            {isSmall ? <FilterButton /> : null}
            <SelectColumnsButton />
            {/* <CreateButton /> */}
            {/* <ExportButton /> */}
            {/* <ListViewButton /> */}
        </TopToolbar>
    );
};

const UserList = () => {
    const notify = useNotify();
    const { canAccess: canWriteUser } = useCanAccess({
        action: 'write',
        resource: 'user',
    });
    const handleOnClick = e => {
        e.stopPropagation();
        navigator.clipboard.writeText(e.target.innerText);
        //notify(e.target.innerText + ' kopioitu leikepöydälle.', { type: 'info' });
        notify(
            <Alert severity="info">
                {e.target.innerText} kopioitu leikepöydälle.
            </Alert>
        );
    };

    const isXsmall = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down('sm')
    );
    const sortable = [
        'public.firstname',
        'public.lastname',
        'username',
        'last_login',
        'registered',
    ];

    const translate = useTranslate();

    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));

    return (
        <List
            filters={isSmall ? userFilters : undefined}
            sort={{ field: 'registered', order: 'DESC' }}
            filter={{
                application: '13b312b5-367c-4c74-bb77-bdb34df4e721',
                need_count: true,
                sessions: false,
            }}
            perPage={50}
            pagination={
                <Pagination
                    rowsPerPageOptions={[
                        10,
                        25,
                        50,
                        100,
                        200,
                        { label: 'Kaikki (max 500)', value: 500 },
                    ]}
                />
            }
            aside={<UserListAside />}
            actions={<ListActions />}
            //pagination={false ? <CustomPagination /> : undefined}
        >
            <WithListContext
                render={({ isLoading, isFetching, data }) =>
                    isLoading || isFetching ? (
                        <DatagridLoading nbChildren={6} nbFakeLines={8} />
                    ) : (
                        <DatagridConfigurable
                            /*
                            rowSx={record => ({
                                color:
                                    record.status === 0
                                        ? 'none'
                                        : 'rgba(255, 0, 0, 0.05)',
                            })}
                            */
                            omit={['guid']}
                            //optimized
                            rowClick="edit"
                            //rowClick={(id: any, basePath: any, record: any) => {window.open("https://oppimateriaalit-aspa.otava.fi/#/teacher/"+id, "_blank");return false;}}
                            bulkActionButtons={
                                canWriteUser ? (
                                    <>
                                        <BulkChangeRoleButton />
                                        <BulkChangeEnvsButton />
                                        <BulkDeleteButton />
                                    </>
                                ) : (
                                    false
                                )
                            }
                            sx={{
                                '& .column-groups': {
                                    md: { display: 'none' },
                                    lg: { display: 'table-cell' },
                                },
                            }}
                        >
                            {/* {columns} */}
                            <TextField
                                source="guid"
                                key="guid"
                                sortable={sortable.includes('guid')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.firstname"
                                key="firstname"
                                sortable={sortable.includes('public.firstname')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.lastname"
                                key="lastname"
                                sortable={sortable.includes('public.lastname')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="username"
                                key="username"
                                sortable={sortable.includes('username')}
                                onClick={handleOnClick}
                            />
                            <DateField
                                source="last_login"
                                key="last_login"
                                showTime={true}
                                locales="fi-FI"
                                sortable={sortable.includes('last_login')}
                                sortByOrder="DESC"
                            />
                            <DateField
                                source="registered"
                                key="registered"
                                showTime={false}
                                locales="fi-FI"
                                sortable={sortable.includes('registered')}
                                sortByOrder="DESC"
                            />
                            <FunctionField
                                label="Rooli"
                                key="role"
                                sortable={false}
                                render={record => {
                                    const app = record?.application?.find(
                                        a => a.id === ADMIN_APP_GUID
                                    );
                                    return app?.userrights
                                        ? USERRIGHTS_MAP[app.userrights] ||
                                              app.userrights
                                        : '';
                                }}
                            />
                            <FunctionField
                                label="Ympäristöt"
                                key="envs"
                                sortable={false}
                                render={record => {
                                    const app = record?.application?.find(
                                        a => a.id === ADMIN_APP_GUID
                                    );
                                    const envs = app?.envs;
                                    if (
                                        !envs ||
                                        !Array.isArray(envs) ||
                                        envs.length === 0
                                    )
                                        return '';
                                    return envs
                                        .map(e => ENVS_MAP[e] || e)
                                        .join(', ');
                                }}
                            />
                            <SourceNameField label="Lähde" size="small" />
                        </DatagridConfigurable>
                    )
                }
            />
        </List>
    );
};

export default UserList;
